import React, { useState, useEffect } from "react";
import { firestore, storage } from "../../firebase-config";
import { doc, collection, getDocs, setDoc, addDoc, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const AddFacultyNonFaculty = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [facultyList, setFacultyList] = useState([]);
  const [nonFacultyList, setNonFacultyList] = useState([]);

  // Fetch all departments from Firestore
  useEffect(() => {
    const fetchDepartments = async () => {
      const snapshot = await getDocs(collection(firestore, "departments"));
      const deptList = [];
      snapshot.forEach((doc) => deptList.push(doc.id)); // Assume department names are document IDs
      setDepartments(deptList);
    };
    fetchDepartments();
  }, []);

  // Add Faculty
  const addFaculty = () =>
    setFacultyList([
      ...facultyList,
      {
        id: "",
        name: "",
        designation: "",
        qualification: "",
        interests: "",
        image: null,
      },
    ]);

  // Add Non-Faculty
  const addNonFaculty = () =>
    setNonFacultyList([
      ...nonFacultyList,
      {
        id: "",
        name: "",
        image: null,
      },
    ]);

  // Handle input change for faculty and non-faculty
  const handleInputChange = (list, setList, index, field, value) => {
    const updatedList = [...list];
    updatedList[index][field] = value;
    setList(updatedList);
  };

  // Upload Image to Firebase Storage
  const uploadImage = async (file, folder) => {
    const imageRef = ref(storage, `${selectedDepartment}/${folder}/${file.name}`);
    await uploadBytes(imageRef, file);
    return await getDownloadURL(imageRef);
  };

  // Check if ID exists in Firestore
  const checkIfIdExists = async (id, type) => {
    const departmentDoc = doc(firestore, "departments", selectedDepartment);
    const subcollection = type === "faculty" ? "faculty" : "nonFaculty";
    const q = query(collection(departmentDoc, subcollection), where("id", "==", id));
    const snapshot = await getDocs(q);
    return !snapshot.empty; // Returns true if ID exists
  };

  // Save Faculty and Non-Faculty to Firestore
  const saveData = async () => {
    if (!selectedDepartment) {
      alert("Please select a department.");
      return;
    }

    try {
      const departmentDoc = doc(firestore, "departments", selectedDepartment);

      // Save Faculty Data
      const facultyCollection = collection(departmentDoc, "faculty");
      for (const faculty of facultyList) {
        if (!faculty.id) {
          alert("Please enter an ID for all faculty.");
          return;
        }
        const idExists = await checkIfIdExists(faculty.id, "faculty");
        if (idExists) {
          alert(`Faculty ID ${faculty.id} already exists.`);
          return;
        }

        const facultyImageUrl = faculty.image
          ? await uploadImage(faculty.image, "faculty_images")
          : null;
        await addDoc(facultyCollection, {
          id: faculty.id,
          name: faculty.name,
          designation: faculty.designation,
          qualification: faculty.qualification,
          interests: faculty.interests,
          imageUrl: facultyImageUrl,
        });
      }

      // Save Non-Faculty Data
      const nonFacultyCollection = collection(departmentDoc, "nonFaculty");
      for (const nonFaculty of nonFacultyList) {
        if (!nonFaculty.id) {
          alert("Please enter an ID for all non-faculty.");
          return;
        }
        const idExists = await checkIfIdExists(nonFaculty.id, "nonFaculty");
        if (idExists) {
          alert(`Non-Faculty ID ${nonFaculty.id} already exists.`);
          return;
        }

        const nonFacultyImageUrl = nonFaculty.image
          ? await uploadImage(nonFaculty.image, "non_faculty_images")
          : null;
        await addDoc(nonFacultyCollection, {
          id: nonFaculty.id,
          name: nonFaculty.name,
          imageUrl: nonFacultyImageUrl,
        });
      }

      alert("Data uploaded successfully!");
      setFacultyList([]);
      setNonFacultyList([]);
    } catch (error) {
      console.error("Error uploading data: ", error);
      alert("Error uploading data.");
    }
  };

  return (
    <div>
      <h1>Add Faculty and Non-Faculty</h1>

      {/* Department Dropdown */}
      <label>Select Department: </label>
      <select
        value={selectedDepartment}
        onChange={(e) => setSelectedDepartment(e.target.value)}
      >
        <option value="">--Select Department--</option>
        {departments.map((dept) => (
          <option key={dept} value={dept}>
            {dept}
          </option>
        ))}
      </select>

      {/* Faculty Details */}
      <h2>Faculty Details</h2>
      {facultyList.map((faculty, index) => (
        <div key={index}>
          <input
            type="text"
            placeholder="ID"
            value={faculty.id}
            onChange={(e) =>
              handleInputChange(facultyList, setFacultyList, index, "id", e.target.value)
            }
          />
          <input
            type="text"
            placeholder="Name"
            value={faculty.name}
            onChange={(e) =>
              handleInputChange(facultyList, setFacultyList, index, "name", e.target.value)
            }
          />
          <input
            type="text"
            placeholder="Designation"
            value={faculty.designation}
            onChange={(e) =>
              handleInputChange(
                facultyList,
                setFacultyList,
                index,
                "designation",
                e.target.value
              )
            }
          />
          <input
            type="text"
            placeholder="Qualification"
            value={faculty.qualification}
            onChange={(e) =>
              handleInputChange(
                facultyList,
                setFacultyList,
                index,
                "qualification",
                e.target.value
              )
            }
          />
          <input
            type="text"
            placeholder="Interests"
            value={faculty.interests}
            onChange={(e) =>
              handleInputChange(
                facultyList,
                setFacultyList,
                index,
                "interests",
                e.target.value
              )
            }
          />
          <input
            type="file"
            onChange={(e) =>
              handleInputChange(
                facultyList,
                setFacultyList,
                index,
                "image",
                e.target.files[0]
              )
            }
          />
        </div>
      ))}
      <button onClick={addFaculty}>Add Faculty</button>

      {/* Non-Faculty Details */}
      <h2>Non-Faculty Details</h2>
      {nonFacultyList.map((nonFaculty, index) => (
        <div key={index}>
          <input
            type="text"
            placeholder="ID"
            value={nonFaculty.id}
            onChange={(e) =>
              handleInputChange(
                nonFacultyList,
                setNonFacultyList,
                index,
                "id",
                e.target.value
              )
            }
          />
          <input
            type="text"
            placeholder="Name"
            value={nonFaculty.name}
            onChange={(e) =>
              handleInputChange(
                nonFacultyList,
                setNonFacultyList,
                index,
                "name",
                e.target.value
              )
            }
          />
          <input
            type="file"
            onChange={(e) =>
              handleInputChange(
                nonFacultyList,
                setNonFacultyList,
                index,
                "image",
                e.target.files[0]
              )
            }
          />
        </div>
      ))}
      <button onClick={addNonFaculty}>Add Non-Faculty</button>

      {/* Save Button */}
      <button onClick={saveData}>Save</button>
    </div>
  );
};

export default AddFacultyNonFaculty;
